
import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useParams, useNavigate } from 'react-router-dom';
import {
    User, Phone, Mail, MapPin, Briefcase, Calendar,
    CreditCard, ArrowLeft, BadgeCheck, AlertCircle,
    Heart, Flag, Wallet, Landmark, FileText,
    X, Edit, Shield, ShieldCheck, CheckCircle, AlertTriangle, UserPlus, Users
} from 'lucide-react';
import API_BASE_URL from '../Config';
import { useAuth } from '../contexts/AuthContext';
import SocioObligaciones from './socio-components/SocioObligaciones';
import SocioCuentas from './socio-components/SocioCuentas';
import SocioCreditos from './socio-components/SocioCreditos';
import SocioDocumentos from './socio-components/SocioDocumentos';
import SocioCompromisos from './socio-components/SocioCompromisos';
import SocioAuditoria from './SocioAuditoria';

export default function SocioDetalle() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token, user } = useAuth();
    const [activeTab, setActiveTab] = useState('cuentas');

    const [socio, setSocio] = useState(null);
    const [cuotas, setCuotas] = useState([]);
    const [documentos, setDocumentos] = useState([]);
    const [loadingSocio, setLoadingSocio] = useState(true);
    const [loadingCuotas, setLoadingCuotas] = useState(false);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [obligaciones, setObligaciones] = useState([]);
    const [loadingObligaciones, setLoadingObligaciones] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [error, setError] = useState(null);

    const DataCell = ({ label, value, icon: Icon, className = "" }) => (
        <div className={`flex flex-col ${className}`}>
            <div className="flex items-center gap-1.5 mb-1">
                {Icon && <Icon className="w-3 h-3 text-slate-400" />}
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none">{label}</span>
            </div>
            <div className="text-sm font-bold text-slate-900 tracking-tight leading-tight">
                {value || '-'}
            </div>
        </div>
    );

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric', month: '2-digit', day: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-PY', {
            style: 'currency', currency: 'PYG',
            minimumFractionDigits: 0, maximumFractionDigits: 2
        }).format(amount || 0);
    };

    const handleAprobar = async () => {
        if (socio.estado?.id === 1) return;

        // Validación exacta de edad y tutoría
        const birthDate = socio.personaFisica?.fechaNacimiento;
        let isMinor = false;
        if (birthDate) {
            const age = Math.floor((new Date() - new Date(birthDate)) / (365.25 * 24 * 60 * 60 * 1000));
            isMinor = age < 18;
        }

        const isJuniorByTipo = socio.tipoSocio?.descripcion?.toUpperCase().includes('JUVENIL');
        const isJuvenil = isMinor || isJuniorByTipo;
        const hasTutor = socio.tutores && socio.tutores.length > 0;

        if (isJuvenil && !hasTutor) {
            Swal.fire({
                icon: 'warning',
                title: 'Vinculación Requerida',
                text: 'Este socio es menor de edad y NO tiene un tutor vinculado. Debe editar el socio y agregar un tutor antes de aprobar.',
                confirmButtonColor: '#1e293b'
            });
            return;
        }

        try {
            const result = await Swal.fire({
                title: `¿Confirmar Aprobación Final?`,
                html: `<p class="text-sm">Se activará a <b>${socio.personaFisica?.nombres || socio.personaJuridica?.razonSocial}</b> como socio activo.</p>`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#10B981',
                confirmButtonText: 'Sí, Aprobar y Activar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                Swal.fire({ title: 'Procesando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
                const response = await fetch(`${API_BASE_URL}/socio/${socio.id}/aprobar`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ observacion: 'Aprobación desde Expediente Técnico' })
                });

                if (!response.ok) {
                    let errorMessage = 'No se pudo completar la aprobación.';
                    try {
                        const errorData = await response.json();
                        errorMessage = errorData.message || errorMessage;
                    } catch (e) {
                        const textError = await response.text();
                        if (textError) errorMessage = textError;
                    }
                    throw new Error(errorMessage);
                }

                Swal.fire('¡Socio Activo!', 'El socio ha sido habilitado correctamente.', 'success');
                window.location.reload();
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo Aprobar',
                text: error.message,
                confirmButtonColor: '#1e293b'
            });
        }
    };

    useEffect(() => {
        const fetchSocio = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/socio/${id}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!response.ok) throw new Error('No se pudo cargar el socio');
                const data = await response.json();
                setSocio(data);
            } catch (err) { setError(err.message); } finally { setLoadingSocio(false); }
        };
        if (id) fetchSocio();
    }, [id, token]);

    useEffect(() => {
        const fetchCuotas = async () => {
            setLoadingCuotas(true);
            try {
                const response = await fetch(`${API_BASE_URL}/creditos/socio/${id}?page=${currentPage}&size=10&sort=fechaVencimiento,asc&estado=PENDIENTE`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (response.ok) {
                    const data = await response.json();
                    setCuotas(data.content || []);
                    setTotalPages(data.totalPages || 0);
                }
            } catch (err) { console.error(err); } finally { setLoadingCuotas(false); }
        };

        const fetchDocumentos = async () => {
            setLoadingDocs(true);
            try {
                const response = await fetch(`${API_BASE_URL}/socio/${id}/documentos`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (response.ok) setDocumentos(await response.json());
            } catch (err) { console.error(err); } finally { setLoadingDocs(false); }
        };

        const fetchObligaciones = async () => {
            setLoadingObligaciones(true);
            try {
                const response = await fetch(`${API_BASE_URL}/obligaciones/socio/${id}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (response.ok) setObligaciones(await response.json());
            } catch (err) { console.error(err); } finally { setLoadingObligaciones(false); }
        };

        if (id && activeTab === 'creditos') fetchCuotas();
        if (id && activeTab === 'documentos') fetchDocumentos();
        if (id && activeTab === 'obligaciones') fetchObligaciones();
    }, [id, currentPage, activeTab, token]);

    if (loadingSocio) return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100">
            <div className="flex flex-col items-center gap-4">
                <div className="animate-spin h-10 w-10 border-4 border-slate-900 border-t-transparent rounded-full" />
                <p className="text-slate-500 font-black text-[9px] uppercase tracking-widest">Abriendo Expediente...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">
            <div className="enterprise-card p-10 max-w-md text-center">
                <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
                <h2 className="text-xl font-black text-slate-800 tracking-tight">Error de Acceso</h2>
                <p className="text-slate-400 text-xs mb-6">{error}</p>
                <button onClick={() => navigate(-1)} className="px-6 py-2 bg-slate-900 text-white rounded-lg text-xs font-black uppercase">Regresar</button>
            </div>
        </div>
    );

    const isFisica = !!socio.personaFisica;
    const isJunior = socio.tipoSocio?.descripcion?.toUpperCase().includes('JUVENIL');
    const isPending = [3, 4, 6].includes(socio.estado?.id);
    const isRechazado = socio.estado?.id === 5;

    // Helper para el Total de Ingresos
    const totalIngresos = socio.perfilEconomico?.reduce((acc, curr) => acc + (curr.montoMensualPromedio || 0), 0) || socio.ingreso || 0;

    return (
        <div className="p-4 md:p-8 bg-slate-100 min-h-screen animate-premium-in">
            <div className="max-w-[1500px] mx-auto space-y-6">

                {/* BARRA DE ACCIÓN TÉCNICA (HEADER) */}
                <div className="bg-slate-800 p-4 rounded-2xl shadow-xl flex flex-col md:flex-row justify-between items-center gap-4 border-b-4 border-uni-teal">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="w-10 h-10 bg-slate-700 text-white flex items-center justify-center rounded-xl hover:bg-slate-600 transition-all shadow-inner"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-lg font-black text-white tracking-tight uppercase">Expediente Técnico del Socio</h1>
                                {isJunior && (
                                    <span className="bg-blue-500/20 text-blue-400 border border-blue-500/30 px-3 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest">
                                        Socio Juvenil
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Legajo #S-{socio.id}</span>
                                <span className="w-1 h-1 bg-slate-600 rounded-full" />
                                <span className={`text-[9px] font-black uppercase tracking-widest ${socio.estado?.id === 1 ? 'text-emerald-400' :
                                        isRechazado ? 'text-rose-400' : 'text-amber-400'
                                    }`}>
                                    Estado: {socio.estado?.descripcion}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        {isPending && (
                            <button
                                onClick={handleAprobar}
                                className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all text-[10px] font-black uppercase tracking-widest shadow-lg shadow-emerald-900/20"
                            >
                                <CheckCircle className="w-4 h-4" /> Aprobar y Activar Socio
                            </button>
                        )}
                        {!isPending && !isRechazado && (
                            <button
                                onClick={() => navigate(`/socios/editar/${socio.id}`)}
                                className="flex items-center gap-2 px-6 py-2.5 bg-white border border-slate-200 text-slate-800 rounded-xl hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest shadow-sm"
                            >
                                <Edit className="w-4 h-4" /> Editar Datos
                            </button>
                        )}
                    </div>
                </div>

                {/* NIVEL 1: IDENTIDAD OPERATIVA */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-6">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center border-2 border-slate-200 shrink-0">
                            <User className="w-8 h-8 text-slate-400" />
                        </div>
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl font-black text-slate-800 tracking-tight uppercase leading-none">
                                    {isFisica ? `${socio.personaFisica?.nombres} ${socio.personaFisica?.apellidos}` : socio.personaJuridica?.razonSocial}
                                </h2>
                                {socio.estado?.id === 1 && <BadgeCheck className="w-5 h-5 text-emerald-500" />}
                            </div>
                            <div className="flex flex-wrap items-center gap-y-1 gap-x-4">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-black text-slate-400 uppercase">Documento:</span>
                                    <span className="text-xs font-black text-slate-600 tracking-tighter">
                                        {isFisica ? socio.personaFisica?.numeroDocumento : socio.personaJuridica?.ruc}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-black text-slate-400 uppercase">Socio Nro:</span>
                                    <span className="text-xs font-black text-slate-600 tracking-tighter">#{socio.numeroSocio || socio.id}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-black text-slate-400 uppercase">Nacionalidad:</span>
                                    <span className="text-xs font-black text-slate-600 tracking-tighter">
                                        {socio.personaFisica?.nacionalidad?.pais || socio.personaJuridica?.nacionalidad?.pais || 'PARAGUAYA'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                        <div className="flex flex-col items-end">
                            <span className="text-[9px] font-black text-slate-400 uppercase">Socio Proponente</span>
                            <span className="text-xs font-black text-slate-700 uppercase tracking-tighter">
                                {socio.proponente ? socio.proponente.nombreCompleto : 'Ingreso Directo'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* NIVEL 1.5: INFORMACIÓN PERSONAL (MODO LECTURA) */}
                {isFisica && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-8">
                            <DataCell label="Fecha de Nacimiento" value={formatDate(socio.personaFisica?.fechaNacimiento)} icon={Calendar} />
                            <DataCell label="Lugar de Nacimiento" value={socio.personaFisica?.lugarNacimiento} icon={MapPin} />
                            <DataCell label="Estado Civil" value={socio.personaFisica?.estadoCivil?.descripcion} icon={Heart} />
                            <DataCell label="Género" value={socio.personaFisica?.genero?.descripcion} icon={User} />
                        </div>

                        {/* LISTA DE TUTORES SI ES MENOR O JUVENIL */}
                        {(socio.tutores && socio.tutores.length > 0) && (
                            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6">
                                <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                                    <Users className="w-4 h-4" /> Responsables / Tutores Legales Vinculados
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {socio.tutores.map((t, idx) => (
                                        <div key={idx} className="bg-white p-4 rounded-xl border border-blue-100 flex items-center justify-between">
                                            <div>
                                                <p className="text-[10px] font-black text-blue-500 uppercase tracking-tighter">
                                                    {t.parentesco?.nombre || 'TUTOR'} {t.esPrincipal ? '(PRINCIPAL)' : ''}
                                                </p>
                                                <p className="text-sm font-black text-slate-800 uppercase tracking-tight">
                                                    {t.tutor?.nombreCompleto || (t.tutor?.personaFisica ? `${t.tutor.personaFisica.nombres} ${t.tutor.personaFisica.apellidos}` : `Socio #${t.tutor?.id}`)}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">ID Socio</span>
                                                <p className="text-xs font-black text-slate-600">#{t.tutor?.id}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* BANNER DE RECHAZO (SI EXISTE) */}
                {isRechazado && (
                    <div className="bg-rose-50 border-2 border-rose-200 rounded-2xl p-6 flex items-center gap-6 shadow-lg shadow-rose-900/5">
                        <div className="w-12 h-12 bg-rose-600 rounded-xl flex items-center justify-center shadow-lg shadow-rose-600/20 shrink-0">
                            <AlertTriangle className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-rose-900 font-black text-sm uppercase tracking-tight mb-1">Solicitud Rechazada</h3>
                            <p className="text-xs font-bold text-rose-700 uppercase">{socio.observacionEstado || 'Sin observación técnica.'}</p>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* COLUMNA IZQUIERDA (DATOS PRINCIPALES) */}
                    <div className="lg:col-span-8 space-y-6">

                        {/* FICHA LABORAL Y PROFESIONAL */}
                        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200">
                            <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-[11px] font-black text-slate-700 uppercase tracking-[0.2em]">Datos Laborales y Profesionales</h3>
                                </div>
                                <button
                                    onClick={() => navigate(`/socios/editar/${socio.id}`)}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 text-slate-500 rounded-lg hover:text-uni-teal hover:border-uni-teal transition-all text-[9px] font-black uppercase tracking-widest shadow-sm"
                                >
                                    <Edit className="w-3 h-3" /> Editar Datos
                                </button>
                            </div>

                            <div className="p-8 space-y-8">
                                {/* GRUPO: IDENTIDAD LABORAL */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                    <DataCell label="Profesión / Ocupación" value={socio.datosLaborales?.profesion?.nombre || socio.personaFisica?.profesionOcupacion} />
                                    <DataCell label="Empresa / Institución" value={socio.datosLaborales?.empresa} />
                                    <DataCell label="Cargo / Función" value={socio.datosLaborales?.tipoOcupacion || 'PERSONAL ADMINISTRATIVO'} />
                                </div>

                                {/* GRUPO: TIEMPO Y ESTABILIDAD */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-6 border-t border-slate-50">
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none">Antigüedad Laboral</span>
                                            {(socio.datosLaborales?.rangoAntiguedad?.descripcion?.includes('Años') ||
                                                socio.datosLaborales?.rangoAntiguedad?.descripcion?.includes('12 meses') ||
                                                socio.datosLaborales?.rangoAntiguedad?.id >= 3) && (
                                                    <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter">Estable</span>
                                                )}
                                        </div>
                                        <div className="text-sm font-bold text-slate-900 tracking-tight leading-tight">
                                            {socio.datosLaborales?.rangoAntiguedad?.descripcion || 'No especificada'}
                                        </div>
                                    </div>
                                    <DataCell label="Fecha de Ingreso" value={formatDate(socio.fechaIngresoCooperativa)} />
                                    <DataCell label="Estado Laboral" value={socio.estado?.id === 1 ? 'ACTIVO' : 'EN VERIFICACIÓN'} />
                                </div>

                                {/* GRUPO: CONTACTO LABORAL Y SUCURSAL */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-6 border-t border-slate-50">
                                    <DataCell label="Teléfono Empresa" value={socio.datosLaborales?.telefono || '-'} />
                                    <DataCell label="Interno / Referencia" value="105 (SISTEMAS)" />
                                    <DataCell label="Sucursal Laboral" value="CENTRAL - CASA MATRIZ" />
                                </div>
                            </div>
                        </div>

                        {/* NIVEL 2.5: RIESGO Y FIDELIDAD (COMPACTO) */}
                        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200">
                            <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-8">
                                <DataCell label="Calificación Riesgo" value={socio.calificacionRiesgo?.codigo || 'A'} />
                                <DataCell label="Nivel Fidelidad" value={socio.nivelFidelidad?.descripcion || 'BRONCE'} />
                                <DataCell label="Categoría de Socio" value={socio.tipoSocio?.descripcion} />
                                <DataCell label="Tipo Persona" value={socio.tipoPersona?.valor || (isFisica ? 'FISICA' : 'JURIDICA')} />
                            </div>
                        </div>

                        {/* NIVEL 3: CAPACIDAD ECONÓMICA (INGRESOS) */}
                        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200">
                            <div className="bg-slate-50 px-6 py-3 border-b border-slate-100 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Wallet className="w-4 h-4 text-slate-400" />
                                    <h3 className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Capacidad Económica (Ingresos)</h3>
                                </div>
                            </div>
                            <div className="p-0">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50/50 border-b border-slate-100">
                                            <th className="px-6 py-3 text-[9px] font-black text-slate-400 uppercase tracking-widest">Fuente de Ingreso / Empresa</th>
                                            <th className="px-6 py-3 text-[9px] font-black text-slate-400 uppercase tracking-widest">Tipo</th>
                                            <th className="px-6 py-3 text-[9px] font-black text-slate-400 uppercase tracking-widest text-right">Monto (Gs.)</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {socio.perfilEconomico && socio.perfilEconomico.length > 0 ? (
                                            socio.perfilEconomico.map((ingreso, idx) => (
                                                <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                                    <td className="px-6 py-4 text-xs font-black text-slate-700 uppercase tracking-tight">{ingreso.empresa || ingreso.descripcionFuente || 'Ingreso Declarado'}</td>
                                                    <td className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase">{ingreso.tipoIngreso?.descripcion || 'OTROS'}</td>
                                                    <td className="px-6 py-4 text-xs font-black text-slate-800 text-right font-mono">{formatCurrency(ingreso.montoMensualPromedio)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td className="px-6 py-4 text-xs font-black text-slate-700 uppercase tracking-tight">Declaración Única</td>
                                                <td className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase">GENERAL</td>
                                                <td className="px-6 py-4 text-xs font-black text-slate-800 text-right font-mono">{formatCurrency(socio.ingreso)}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-slate-800 text-white border-t-2 border-uni-teal">
                                            <td colSpan="2" className="px-6 py-4 text-[10px] font-black uppercase tracking-widest">Total Ingresos Disponibles Mensuales</td>
                                            <td className="px-6 py-4 text-sm font-black text-right tracking-tighter font-mono">
                                                {formatCurrency(totalIngresos)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* COLUMNA DERECHA (CONTACTO Y SERVICIOS) */}
                    <div className="lg:col-span-4 space-y-6">

                        {/* DATOS DE CONTACTO Y UBICACIÓN */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-6">
                            <div className="flex items-center gap-2 mb-2">
                                <MapPin className="w-4 h-4 text-slate-400" />
                                <h3 className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Contacto y Ubicación</h3>
                            </div>
                            <div className="space-y-4">
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <div className="flex items-center gap-3 mb-1">
                                        <Phone className="w-3.5 h-3.5 text-slate-400" />
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-tight">Teléfonos / Celular</span>
                                    </div>
                                    <p className="text-sm font-black text-slate-800 tracking-tighter">
                                        {socio.contactos?.find(c => c.tipoContacto?.toString().toUpperCase().includes('CELULAR'))?.valor || socio.telefono || '-'}
                                    </p>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <div className="flex items-center gap-3 mb-1">
                                        <Mail className="w-3.5 h-3.5 text-slate-400" />
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-tight">Correo Electrónico</span>
                                    </div>
                                    <p className="text-sm font-black text-slate-800 lowercase tracking-tighter">
                                        {socio.contactos?.find(c => c.tipoContacto?.toString().toUpperCase().includes('EMAIL'))?.valor || socio.email || '-'}
                                    </p>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <div className="flex items-center gap-3 mb-1">
                                        <MapPin className="w-3.5 h-3.5 text-slate-400" />
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-tight">Domicilio Real</span>
                                    </div>
                                    <p className="text-[11px] font-black text-slate-800 uppercase leading-tight tracking-tighter">
                                        {socio.direcciones?.[0]?.direccionExacta || socio.direccion || '-'}
                                    </p>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase mt-1">
                                        {socio.direcciones?.[0]?.barrio?.ciudad?.nombre} - {socio.direcciones?.[0]?.barrio?.ciudad?.departamento?.nombre}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* NIVEL 4: SERVICIOS VERIFICADOS (ANDE/ESSAP) */}
                        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200">
                            <div className="bg-slate-50 px-6 py-3 border-b border-slate-100 flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-slate-400" />
                                <h3 className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Servicios Verificados</h3>
                            </div>
                            <div className="p-6 space-y-4">
                                {socio.servicios && socio.servicios.length > 0 ? (
                                    socio.servicios.map((srv, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 group hover:border-uni-teal transition-all shadow-sm">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-slate-400 border border-slate-100 shadow-sm">
                                                    {srv.tipoServicio === 'ANDE' ? <Flag className="w-5 h-5 text-amber-500" /> : <MapPin className="w-5 h-5 text-blue-500" />}
                                                </div>
                                                <div>
                                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-tight">{srv.tipoServicio}</p>
                                                    <p className="text-xs font-black text-slate-700 tracking-tighter">NIS: {srv.identificadorServicio}</p>
                                                </div>
                                            </div>
                                            {(srv.latitud && srv.longitud) && (
                                                <button
                                                    onClick={() => window.open(`https://www.google.com/maps?q=${srv.latitud},${srv.longitud}`, '_blank')}
                                                    className="p-2 bg-white text-slate-400 hover:text-uni-teal rounded-lg border border-slate-100 shadow-sm transition-all"
                                                    title="Ver ubicación en GPS"
                                                >
                                                    <MapPin className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-4">
                                        <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest">Sin servicios registrados</p>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>

                {/* TUTORES (SI ES JUNIOR) */}
                {isJunior && socio.tutores && socio.tutores.length > 0 && (
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200">
                        <div className="bg-blue-50 px-6 py-3 border-b border-blue-100 flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4 text-blue-500" />
                            <h3 className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Tutores Legales y Responsables</h3>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                            {socio.tutores.map(t => (
                                <div key={t.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-blue-200 transition-all">
                                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-blue-500 border border-slate-100">
                                        <User className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-[8px] font-black text-blue-500 uppercase tracking-tight">{t.parentesco?.nombre}</p>
                                        <p className="text-xs font-black text-slate-800 uppercase tracking-tight">{t.tutor?.nombreCompleto}</p>
                                        <p className="text-[9px] font-bold text-slate-400 mt-0.5 tracking-tighter">CI: {t.tutor?.personaFisica?.numeroDocumento || t.tutor?.numeroDocumento}</p>
                                    </div>
                                    {t.esPrincipal && <BadgeCheck className="w-4 h-4 text-emerald-500 ml-auto" />}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* PESTAÑAS DE DETALLES (HISTORIAL) */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="flex border-b border-slate-100 bg-slate-50">
                        {[
                            { id: 'cuentas', label: 'Cuentas Bancarias', icon: Landmark },
                            { id: 'obligaciones', label: 'Obligaciones', icon: FileText },
                            ...(socio.estado?.id === 1 ? [
                                { id: 'creditos', label: 'Préstamos', icon: CreditCard },
                                { id: 'compromisos', label: 'Compromisos', icon: Phone },
                            ] : []),
                            { id: 'documentos', label: 'Legajo Digital', icon: FileText },
                            ...(user?.permisos?.includes('auditoria.read') || user?.roles?.includes('admin') || user?.permisos?.includes('admin')
                                ? [{ id: 'auditoria', label: 'Auditoría', icon: Shield }]
                                : [])
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex-1 flex items-center justify-center gap-2 py-4 text-[9px] font-black uppercase tracking-widest transition-all ${activeTab === tab.id
                                        ? 'bg-white text-uni-teal border-b-2 border-uni-teal'
                                        : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                                    }`}
                            >
                                <tab.icon className="w-3.5 h-3.5" />
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="p-6">
                        {activeTab === 'cuentas' && <SocioCuentas cuentas={socio.cuentas} formatCurrency={formatCurrency} />}
                        {activeTab === 'obligaciones' && <SocioObligaciones obligaciones={obligaciones} loading={loadingObligaciones} socioId={id} formatCurrency={formatCurrency} formatDate={formatDate} />}
                        {activeTab === 'creditos' && <SocioCreditos cuotas={cuotas} loading={loadingCuotas} socioId={id} totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} formatDate={formatDate} formatCurrency={formatCurrency} />}
                        {activeTab === 'compromisos' && <SocioCompromisos socioId={id} socio={socio} token={token} formatCurrency={formatCurrency} formatDate={formatDate} />}
                        {activeTab === 'documentos' && <SocioDocumentos documentos={documentos} loading={loadingDocs} formatDate={formatDate} />}
                        {activeTab === 'auditoria' && <SocioAuditoria socioId={id} />}
                    </div>
                </div>
            </div>
        </div>
    );
}
